import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FiltrosSolicitudes, SolicitudesService } from 'src/app/services/integracion-pos/solicitudes.service';
import { MerchantService } from 'src/app/services/merchant/merchant.service';

/**
 * «Solicitudes de crédito» — consola de CrediTotal.
 *
 * Contrato: `~/Documents/multicomercio/competencia/60-modulo-solicitudes-contrato.md`.
 * Rediseño: documentos 63 (negocio y arquitectura), 64 (el dato a la vista) y 66 (la
 * propuesta aprobada, §7), 2026-09-20 — «mira esos filtros, por Dios, 3.000 filtros».
 *
 * 🔴 Vocabulario (§2 del contrato): la entidad es UNA (`SOL-MT-000123`), pero se
 * rotula distinto según quién mira. Acá, en la consola de CrediTotal, es
 * «Solicitudes de crédito» — CON el apellido, porque ya existe una pantalla
 * «Solicitudes de acceso» y son cosas distintas. En el portal del comercio la
 * misma entidad se llama «Órdenes».
 *
 * 🔴 SE MIRA, NO SE ACTÚA (§3): esta vuelta contesta «¿dónde quedó esta
 * solicitud?», no permite anular, reprocesar ni forzar nada. Por eso no hay
 * botones de acción en la fila, sólo «ver» (la fila entera abre el detalle).
 *
 * 🔴 Los filtros viven en la URL (query params), a propósito: un enlace con
 * filtros se puede compartir tal cual, y «atrás» deshace el último cambio de
 * filtro porque cada cambio empuja una entrada nueva de historial (no se usa
 * `replaceUrl`). La única fuente de verdad de qué se está filtrando es la URL:
 * el formulario nunca dispara la búsqueda directamente, sólo la actualiza.
 * Once filtros se convirtieron en tres: una caja que adivina el campo por la
 * forma de lo escrito, cinco cortes que cuentan el proceso, y comercio/período
 * arriba. Tienda, caja y cajero se filtran tocando la columna. Ninguno de los
 * once query params del backend se borró — sólo cambió quién los escribe.
 *
 * 🔴 2026-09-20: y desde esta vuelta `status`/`etapa` los escribe UN SOLO control
 * —los cinco cortes—, no dos. El reparto se quedó de leer (ver el comentario de
 * `cortes`).
 */
@Component({
  selector: 'app-solicitudes-credito-list',
  templateUrl: './solicitudes-credito-list.component.html',
  styleUrls: ['./solicitudes-credito-list.component.css']
})
export class SolicitudesCreditoListComponent implements OnInit, OnDestroy {

  /** Los cuatro estados que arman el corte «No llegaron» (§7 decisión 3 del
   *  documento 66: se descartó «Cerradas» porque mete a las que salieron bien
   *  con las que no). `CANCELADA` existe en el dominio pero no está en esta
   *  lista ni en el encargo — queda fuera del corte, sólo se ve en «Todas». */
  private readonly ESTADOS_NO_LLEGARON = ['ANULADA', 'VENCIDA', 'RECHAZADA_POR_EL_CLIENTE', 'RECHAZADA_EN_VERIFICACION'];

  /** El estado trabado (documento 66 §7 decisión 1: «Trabada»). `COBRO_INCOMPLETO`
   *  es el que arma el corte rápido; los otros dos etapas fallidas se ven igual
   *  de trabadas en la fila, aunque el corte de arriba sólo dispare con la primera. */
  private readonly ETAPAS_TRABADA = ['COBRO_INCOMPLETO', 'COBRO_FALLIDO', 'CREDITO_FALLIDO'];

  /**
   * Los tres grupos de la franja del reparto, con su color: los tokens `--dd-*` de
   * `styles.css`, nunca un paso literal de Tailwind (`bg-emerald-100`).
   *
   * 🔴 TRES COLORES, NO NUEVE. El mapa anterior daba un color POR ESTADO y cuatro
   * estados compartían el mismo rojo: mientras cada uno tenía su barra con su
   * rótulo al lado se podía leer, pero en una franja apilada dos tramos del mismo
   * color pegados son un solo tramo. Se apila por grupo, que además es como ya
   * estaban agrupados los estados.
   *
   * 🔴 «No llegaron» lleva EXACTAMENTE los estados del corte del mismo nombre, para
   * que el número de la franja y el del corte no se contradigan. `CANCELADA` —que
   * antes entraba en el dibujo y NO en el corte— cae en «Otras», junto con
   * cualquier estado que el servidor mande y esta pantalla todavía no conozca: un
   * estado nuevo del dominio no puede desaparecer del dibujo sin que nadie se
   * entere.
   */
  private readonly GRUPOS_ESTADO: Array<{ titulo: string; estados: string[]; color: string }> = [
    { titulo: 'Terminaron bien', estados: ['CONFIRMADA'], color: 'var(--dd-done)' },
    // 2026-09-25: en ámbar (espera a alguien), no en el cian de la casa vieja: el color vive en el dato.
    { titulo: 'Todavía vivas', estados: ['EN_ESPERA', 'ACEPTADA', 'EN_PROCESO'], color: 'var(--dd-warn)' },
    { titulo: 'No llegaron', estados: this.ESTADOS_NO_LLEGARON, color: 'var(--dd-danger)' },
    // 2026-09-25: las canceladas con su nombre. Caían en «Otras», que no le decía a nadie qué eran.
    { titulo: 'Canceladas', estados: ['CANCELADA'], color: 'var(--dd-muted)' },
  ];

  private subQueryParams?: Subscription;
  private timerBusqueda: any;

  filtros: FiltrosSolicitudes = {};

  /** Lo que hay tecleado en la única caja de búsqueda. No es un campo de
   *  `filtros`: de ahí se DERIVA cuál de los cuatro (`cedula`, `cajero`,
   *  `credito`, `factura`) se enciende, según la forma de lo escrito. */
  busquedaTexto = '';

  resultado: any = null;
  items: any[] = [];
  cargando = true;
  error = '';
  /** `false` sólo cuando el propio servidor dice `retryable:false` (dominio/codigos.js).
   *  Sin ese dato —p.ej. el `{msg}` del middleware de JWT— no hay manera de afirmar que
   *  reintentar no sirve, así que por omisión se ofrece igual. */
  errorReintentable = true;

  comercios: any[] = [];

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _solicitudes: SolicitudesService,
    private _merchant: MerchantService
  ) { }

  ngOnInit(): void {
    this.cargarComercios();
    this.subQueryParams = this.ruta.queryParams.subscribe((qp: Params) => {
      this.filtros = this.leerFiltrosDeUrl(qp);
      this.busquedaTexto = this.reconstruirBusqueda(this.filtros);
      this.cargar();
    });
  }

  ngOnDestroy(): void {
    this.subQueryParams?.unsubscribe();
    clearTimeout(this.timerBusqueda);
  }

  /** Sólo para el filtro de comercio. Los comercios son decenas, no miles: se
   *  traen una vez y quedan en memoria, igual que en la barra lateral. */
  cargarComercios = async (): Promise<void> => {
    try {
      const rep = await this._merchant.getMerchants({});
      this.comercios = rep?.merchants || [];
    } catch { this.comercios = []; }
  };

  // ── URL ⇄ filtros ────────────────────────────────────────────────────────
  //
  // La URL es la única fuente de verdad. Cambiar un campo del formulario no
  // dispara la búsqueda: actualiza la URL, y es la suscripción de arriba —que
  // también corre cuando se aprieta «atrás»— la que carga con esos filtros.

  private leerFiltrosDeUrl = (qp: Params): FiltrosSolicitudes => ({
    cedula: qp['cedula'] || '',
    merchant_code: qp['merchant_code'] || '',
    shop: qp['shop'] || '',
    cash_register: qp['cash_register'] || '',
    cajero: qp['cajero'] || '',
    status: qp['status'] || '',
    etapa: qp['etapa'] || '',
    desde: qp['desde'] || '',
    hasta: qp['hasta'] || '',
    credito: qp['credito'] || '',
    factura: qp['factura'] || '',
    page: qp['page'] ? Number(qp['page']) : 1,
    limit: qp['limit'] ? Number(qp['limit']) : 50,
  });

  /** Escribe `this.filtros` en la URL. Los valores vacíos y los que coinciden
   *  con el valor por omisión no viajan, para que el enlace quede limpio. */
  private actualizarUrl = (opciones: { reiniciarPagina?: boolean } = {}): void => {
    if (opciones.reiniciarPagina !== false) this.filtros.page = 1;
    const qp: any = {};
    const f = this.filtros;
    if (f.cedula) qp.cedula = f.cedula;
    if (f.merchant_code) qp.merchant_code = f.merchant_code;
    if (f.shop) qp.shop = f.shop;
    if (f.cash_register) qp.cash_register = f.cash_register;
    if (f.cajero) qp.cajero = f.cajero;
    if (f.status) qp.status = f.status;
    if (f.etapa) qp.etapa = f.etapa;
    if (f.desde) qp.desde = f.desde;
    if (f.hasta) qp.hasta = f.hasta;
    if (f.credito) qp.credito = f.credito;
    if (f.factura) qp.factura = f.factura;
    if (f.page && f.page !== 1) qp.page = f.page;
    if (f.limit && f.limit !== 50) qp.limit = f.limit;
    this.router.navigate([], { relativeTo: this.ruta, queryParams: qp });
  };

  /** Selects y fechas cambian de una sola vez: no hace falta esperar. */
  onFiltroInmediato = (): void => this.actualizarUrl();

  get hayFiltros(): boolean {
    const f = this.filtros;
    return !!(f.cedula || f.merchant_code || f.shop || f.cash_register || f.cajero ||
              f.status || f.etapa || f.desde || f.hasta || f.credito || f.factura);
  }

  limpiarFiltros = (): void => {
    this.busquedaTexto = '';
    this.filtros = { page: 1, limit: this.filtros.limit };
    this.actualizarUrl();
  };

  // ── la caja de búsqueda que adivina ──────────────────────────────────────
  //
  // 🔴 Documento 66 §1 y documento 63 §3.1: se come cinco filtros (cédula,
  // cajero, crédito, factura y el código). El código de solicitud (`SOL-…`) es
  // el único caso especial: el contrato (documento 60 §4.1) no tiene un query
  // param para buscar por `code` — sólo el detalle lo resuelve por su ruta
  // (`GET .../admin/solicitudes/:code`). No se inventa un filtro que el
  // servidor no entiende: se ofrece abrir esa solicitud directamente.

  /** Reconstruye qué mostrar en la caja al llegar por URL (carga inicial o
   *  «atrás»): el primero de los cuatro campos que venga con valor. */
  private reconstruirBusqueda = (f: FiltrosSolicitudes): string =>
    f.credito || f.factura || f.cajero || f.cedula || '';

  get esCodigoDeSolicitud(): boolean {
    return /^SOL-/i.test((this.busquedaTexto || '').trim());
  }

  get codigoSugerido(): string {
    return (this.busquedaTexto || '').trim().toUpperCase();
  }

  /** El número pelado es el único caso ambiguo (documento 63 §3.1): puede ser
   *  la cédula del cliente —el 90 % de los reclamos, y el valor por omisión—,
   *  la del cajero o un número de crédito sin el prefijo. Sigue ambiguo
   *  mientras la interpretación activa sea la de cédula del cliente. */
  get busquedaEsAmbigua(): boolean {
    const t = (this.busquedaTexto || '').trim();
    return /^\d+$/.test(t) && !!this.filtros.cedula;
  }

  onBusquedaCambio = (): void => {
    clearTimeout(this.timerBusqueda);
    this.timerBusqueda = setTimeout(() => this.aplicarBusqueda(), 450);
  };

  onEnterBusqueda = (): void => {
    clearTimeout(this.timerBusqueda);
    if (this.esCodigoDeSolicitud) { this.abrirPorCodigo(); return; }
    this.aplicarBusqueda();
  };

  abrirPorCodigo = (): void => {
    if (!this.esCodigoDeSolicitud) return;
    this.router.navigate(['/console/solicitudes-credito', this.codigoSugerido]);
  };

  /** Una de las dos veces que hace falta decidir algo (documento 63 §3.1): el
   *  número pelado por omisión va a cédula, y estos dos enlaces lo reinterpretan
   *  sin que haya que borrar y volver a escribir. */
  resolverAmbiguo = (campo: 'cajero' | 'credito'): void => {
    clearTimeout(this.timerBusqueda);
    this.aplicarBusqueda({ forzarCampo: campo });
  };

  private aplicarBusqueda = (opciones: { forzarCampo?: 'cajero' | 'credito' } = {}): void => {
    const texto = (this.busquedaTexto || '').trim();
    this.filtros.cedula = '';
    this.filtros.cajero = '';
    this.filtros.credito = '';
    this.filtros.factura = '';

    if (!texto) { this.actualizarUrl(); return; }

    if (opciones.forzarCampo === 'cajero') {
      this.filtros.cajero = 'V-' + texto.replace(/\D/g, '');
    } else if (opciones.forzarCampo === 'credito') {
      this.filtros.credito = 'CREDIT-' + texto.replace(/\D/g, '');
    } else if (/^SOL-/i.test(texto)) {
      // Se resuelve con el botón «Abrir la solicitud», no como filtro: el
      // listado queda sin ese filtro puesto (ver el comentario de arriba).
    } else if (/^CREDIT-/i.test(texto)) {
      this.filtros.credito = texto.toUpperCase();
    } else if (/^F-/i.test(texto)) {
      this.filtros.factura = texto;
    } else if (/^V-?\d+/i.test(texto)) {
      this.filtros.cedula = 'V-' + texto.replace(/\D/g, '');
    } else {
      // Número pelado (ambiguo) o cualquier otro texto: cédula del cliente,
      // que es el 90 % de los reclamos (documento 63 §3.1).
      this.filtros.cedula = /^\d+$/.test(texto) ? ('V-' + texto) : texto;
    }
    this.actualizarUrl();
  };

  quitarBusqueda = (): void => {
    this.busquedaTexto = '';
    this.filtros.cedula = '';
    this.filtros.cajero = '';
    this.filtros.credito = '';
    this.filtros.factura = '';
    this.actualizarUrl();
  };

  // ── tocar la columna filtra (tienda y caja dejaron de tener cajita) ──────

  filtrarPorTienda = (tienda: any, ev: Event): void => {
    ev.stopPropagation();
    this.filtros.shop = String(tienda);
    this.actualizarUrl();
  };

  filtrarPorCaja = (caja: any, ev: Event): void => {
    ev.stopPropagation();
    this.filtros.cash_register = String(caja);
    this.actualizarUrl();
  };

  /**
   * La cédula del cajero llega en cada fila (`cajero_cedula`) y no se veía, aunque
   * el listado ya sabe filtrar por ella. Ojo con qué es: `cashier_declared`, texto
   * que el cajero teclea al cobrar, NO una atribución probada
   * (`listado-solicitudes.js:aItem`). Se filtra igual —el back normaliza las tres
   * formas en que quedó guardada— pero la pantalla no la presenta como si
   * respondiera por nadie: eso lo dice el `title` del botón.
   */
  filtrarPorCajero = (cedula: any, ev: Event): void => {
    ev.stopPropagation();
    this.filtros.cajero = String(cedula);
    this.actualizarUrl();
  };

  nombreComercio = (code: string): string =>
    this.comercios.find((c) => c.code === code)?.name || code;

  private textoPeriodo = (f: FiltrosSolicitudes): string => {
    if (f.desde && f.hasta) return `Del ${f.desde} al ${f.hasta}`;
    if (f.desde) return `Desde ${f.desde}`;
    return `Hasta ${f.hasta}`;
  };

  /** 🔴 Todo lo filtrado queda como pastilla que se puede quitar (documento 66
   *  §1). `status`/`etapa` no entran mientras los esté mostrando un corte: ya se
   *  ven puestos ahí arriba, y repetirlos como pastilla sería la misma
   *  información dos veces. La excepción, al final, es cuando NINGÚN corte los
   *  muestra. */
  get pastillas(): Array<{ etiqueta: string; quitar: () => void }> {
    const f = this.filtros;
    const out: Array<{ etiqueta: string; quitar: () => void }> = [];
    if (f.cedula) out.push({ etiqueta: `Cliente ${f.cedula}`, quitar: this.quitarBusqueda });
    if (f.cajero) out.push({ etiqueta: `Cajero ${f.cajero}`, quitar: this.quitarBusqueda });
    if (f.credito) out.push({ etiqueta: `Crédito ${f.credito}`, quitar: this.quitarBusqueda });
    if (f.factura) out.push({ etiqueta: `Factura ${f.factura}`, quitar: this.quitarBusqueda });
    if (f.merchant_code) out.push({
      etiqueta: `Comercio: ${this.nombreComercio(f.merchant_code)}`,
      quitar: () => { this.filtros.merchant_code = ''; this.actualizarUrl(); },
    });
    if (f.shop) out.push({
      etiqueta: `Tienda ${f.shop}`,
      quitar: () => { this.filtros.shop = ''; this.actualizarUrl(); },
    });
    if (f.cash_register) out.push({
      etiqueta: `Caja ${f.cash_register}`,
      quitar: () => { this.filtros.cash_register = ''; this.actualizarUrl(); },
    });
    if (f.desde || f.hasta) out.push({
      etiqueta: this.textoPeriodo(f),
      quitar: () => { this.filtros.desde = ''; this.filtros.hasta = ''; this.actualizarUrl(); },
    });
    // 🔴 Desde que el reparto dejó de filtrar, `status` y `etapa` los escribe un solo
    // control: los cinco cortes, y ahí se ven puestos. Pero una URL vieja —o una
    // compartida por alguien que filtró por un estado suelto— puede traer una
    // combinación que no es ninguno de los cinco: sin esta pastilla el filtro estaría
    // puesto, la lista saldría recortada y no habría manera de verlo ni de sacarlo.
    if (this.corteActivo === '') {
      if (f.status) out.push({
        etiqueta: `Estado: ${String(f.status).split(',').map((v) => this.textoEstatus(v)).join(', ')}`,
        quitar: () => { this.filtros.status = ''; this.actualizarUrl(); },
      });
      if (f.etapa) out.push({
        etiqueta: `Etapa: ${this.textoEtapa(f.etapa)}`,
        quitar: () => { this.filtros.etapa = ''; this.actualizarUrl(); },
      });
    }
    return out;
  }

  // ── traer los datos ──────────────────────────────────────────────────────

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = '';
    this.errorReintentable = true;
    try {
      this.resultado = await this._solicitudes.getSolicitudes(this.filtros);
      this.items = this.resultado?.items || [];
    } catch (e: any) {
      this.items = [];
      this.resultado = null;
      this.error = this.mensajeError(e);
      this.errorReintentable = this.esReintentable(e);
    } finally {
      this.cargando = false;
    }
  };

  /**
   * 🔴 El servidor manda DOS formas de error, y ninguna es la que se leía antes
   * (`e?.error?.msg` a secas): la del propio módulo, `{error:{code,message,retryable}}`
   * (`dominio/codigos.js:error`) — p.ej. `NO_ES_CREDITOTAL`, 403 — y la del middleware de
   * JWT/cron, que manda `{msg}` sin envolver. Ninguna coincidía con lo que se leía, así que
   * siempre salía el mensaje genérico con un botón de reintentar que jamás iba a funcionar.
   */
  private mensajeError = (e: any): string =>
    e?.error?.error?.message || e?.error?.msg || 'No se pudieron traer las solicitudes. Intenta de nuevo en un momento.';

  /** Sólo la forma del módulo trae `retryable`. Cuando dice `false` en firme no se ofrece
   *  el botón — sin ese dato, no hay manera de afirmar que reintentar no sirve, así que por
   *  omisión se sigue ofreciendo. */
  private esReintentable = (e: any): boolean => e?.error?.error?.retryable !== false;

  // ── los números del período filtrado ─────────────────────────────────────

  get total(): number { return this.resultado?.total || 0; }
  get resumen(): any { return this.resultado?.resumen || {}; }
  get montoUsd(): number { return this.resumen?.monto_usd || 0; }
  /** 2026-09-25: lo VENDIDO (las confirmadas). «Monto» a secas sumaba también rechazadas y canceladas. */
  get vendidoUsd(): number { return this.resumen?.monto_confirmado_usd || 0; }
  get cobroIncompleto(): number { return this.resumen?.por_etapa?.COBRO_INCOMPLETO || 0; }

  /**
   * 🔴 `por_etapa` viene siempre COMPLETO (todas las etapas, en 0 si ninguna se dio —
   * documento 60 §6, `resumenDesdeFacetas`). Si TODAS están en cero a la vez no hay manera
   * de distinguir «ninguna quedó incompleta» de «nunca se escribió el rastro» — y la
   * pantalla no puede afirmar lo primero sin poder probarlo. El que lo dice en ese caso
   * es `resultadoDelCobro`, con todas las letras — antes era un carril rayado dentro del
   * reparto, que ocupaba siete barras para contar eso.
   */
  get hayRastroDeEtapas(): boolean {
    const porEtapa = this.resumen?.por_etapa || {};
    const claves = Object.keys(porEtapa);
    return claves.length > 0 && claves.some((k) => (porEtapa[k] || 0) > 0);
  }

  // ── los cinco cortes (documento 66 §7 decisión 3) ────────────────────────
  //
  // Leídos en fila cuentan el proceso: nace, el cliente firma, la caja cobra,
  // o se traba, o se cierra mal. Abren siempre en «Todas» — nunca en un corte,
  // porque un tablero que abre vacío esperando que filtres está en la lista
  // negra del estudio de diseño.
  //
  // 🔴 2026-09-20: SON EL ÚNICO CONTROL QUE ESCRIBE `status` Y `etapa`. Hasta hoy
  // lo escribían también las barras del reparto, y hacían lo mismo: «Por firmar»
  // y la barra «En espera» ponían `EN_ESPERA`, «Trabadas» y la barra
  // `COBRO_INCOMPLETO` la misma etapa. De los dos, quedaron los cortes, por tres
  // razones medidas: caben en un renglón contra los ~800 px de la tarjeta del
  // reparto; están escritos en la lengua del proceso («Por cobrar») y no en la
  // del dominio («ACEPTADA»); y son los que llevan el anillo de alerta en
  // «Trabadas», que es el caso por el que esta pantalla existe.

  get cortes(): Array<{ clave: string; texto: string; valor: number }> {
    const porEstado = this.resumen?.por_estado || {};
    const sumarEstados = (claves: string[]) => claves.reduce((s, c) => s + (porEstado[c] || 0), 0);
    return [
      { clave: 'todas', texto: 'Todas', valor: this.total },
      { clave: 'por_firmar', texto: 'Por aceptar', valor: sumarEstados(['EN_ESPERA']) },
      { clave: 'por_cobrar', texto: 'Por cobrar', valor: sumarEstados(['ACEPTADA']) },
      { clave: 'trabadas', texto: 'Trabadas', valor: this.cobroIncompleto },
      { clave: 'no_llegaron', texto: 'No llegaron', valor: sumarEstados(this.ESTADOS_NO_LLEGARON) },
    ];
  }

  private get statusNoLlegaronCsv(): string { return this.ESTADOS_NO_LLEGARON.join(','); }

  get corteActivo(): string {
    const f = this.filtros;
    if (!f.status && !f.etapa) return 'todas';
    if (f.status === 'EN_ESPERA' && !f.etapa) return 'por_firmar';
    if (f.status === 'ACEPTADA' && !f.etapa) return 'por_cobrar';
    if (f.status === 'CONFIRMADA' && f.etapa === 'COBRO_INCOMPLETO') return 'trabadas';
    if (f.status === this.statusNoLlegaronCsv && !f.etapa) return 'no_llegaron';
    // Una combinación que ningún corte arma. Antes pasaba todo el tiempo —era el
    // reparto escribiendo el mismo filtro—; hoy sólo puede venir de una URL vieja o
    // hecha a mano, y para eso `pastillas` la muestra y deja quitarla.
    return '';
  }

  aplicarCorte = (clave: string): void => {
    this.filtros.status = '';
    this.filtros.etapa = '';
    if (clave === 'por_firmar') this.filtros.status = 'EN_ESPERA';
    else if (clave === 'por_cobrar') this.filtros.status = 'ACEPTADA';
    else if (clave === 'trabadas') { this.filtros.status = 'CONFIRMADA'; this.filtros.etapa = 'COBRO_INCOMPLETO'; }
    else if (clave === 'no_llegaron') this.filtros.status = this.statusNoLlegaronCsv;
    // 'todas' deja status y etapa vacíos.
    this.actualizarUrl();
  };

  // ── el reparto: una franja que se lee, no dieciséis botones ───────────────
  //
  // 🔴 REDISEÑO 2026-09-20 — LAS BARRAS DEJARON DE FILTRAR, Y DEJARON DE SER
  // BARRAS. Eran dieciséis botones (nueve estados + siete etapas) dentro de una
  // tarjeta de ~800 px que empujaba la primera fila de la tabla hasta los
  // ~1.000 px: quien entra a buscar una solicitud veía un gráfico y tenía que
  // rodar. Y escribían los MISMOS `status`/`etapa` que los cinco cortes, así
  // que la pantalla tenía dos controles para un solo filtro y ya no sabía cuál
  // estaba puesto (ver `corteActivo`).
  //
  // Lo que queda es una franja de ~100 px con lo que NINGÚN corte dice: la
  // proporción, el desglose de las que no llegaron y el resultado del cobro.
  // Lo que se perdió, dicho en voz alta: ya no se filtra por UN estado suelto
  // desde la pantalla —«Rechazada en verificación», por ejemplo—. La URL lo
  // sigue aceptando (`?status=RECHAZADA_EN_VERIFICACION`) y ahora eso se ve
  // como pastilla y se puede quitar, que antes tampoco pasaba.

  /** 🔴 La cobertura NO se apila con el reparto (documento 66 §2): si el total
   *  no cuadra con la suma de `por_estado`, ese resto va en texto y nunca como
   *  un tramo más de la franja. */
  get coberturaEstado(): number {
    const porEstado = this.resumen?.por_estado || {};
    return Object.keys(porEstado).reduce((s, k) => s + (Number(porEstado[k]) || 0), 0);
  }

  get sinClasificar(): number { return Math.max(0, this.total - this.coberturaEstado); }

  /** Sólo habla cuando hay un hueco. El total ya lo dicen el corte «Todas» y el
   *  pie de la paginación; decirlo una tercera vez no agrega nada. Que de
   *  algunas no se conozca el estado, sí. */
  get coberturaEstadoTexto(): string {
    if (!this.total || !this.sinClasificar) return '';
    const plural = this.sinClasificar === 1 ? 'una' : `${this.sinClasificar}`;
    return `De ${plural} de las ${this.total} del período no se conoce el estado.`;
  }

  /** Los tramos de la franja. Los tres grupos van SIEMPRE, aunque den cero —el
   *  cero se muestra—; «Otras» sólo si tiene algo, porque un grupo que nunca se
   *  llena es ruido. Los porcentajes van sobre lo clasificado, no sobre el
   *  total: si falta cobertura, la franja queda corta a propósito y el renglón
   *  de abajo explica por qué. */
  get gruposEstado(): Array<{ titulo: string; subtotal: number; pct: number; color: string }> {
    const porEstado = this.resumen?.por_estado || {};
    const base = this.coberturaEstado || 1;
    const pct = (n: number) => Math.round((n / base) * 1000) / 10;

    const salida = this.GRUPOS_ESTADO.map((g) => {
      const subtotal = g.estados.reduce((s, c) => s + (porEstado[c] || 0), 0);
      return { titulo: g.titulo, subtotal, pct: pct(subtotal), color: g.color };
    });

    const conocidos = this.GRUPOS_ESTADO.reduce((acc: string[], g) => acc.concat(g.estados), []);
    const otras = Object.keys(porEstado)
      .filter((k) => conocidos.indexOf(k) === -1)
      .reduce((s, k) => s + (Number(porEstado[k]) || 0), 0);
    if (otras > 0) salida.push({ titulo: 'Otras', subtotal: otras, pct: pct(otras), color: 'var(--dd-muted)' });

    return salida;
  }

  /** La franja es un `role="img"`: el lector de pantalla necesita el reparto
   *  completo en una frase, no cuatro `<span>` sin contexto. */
  get ariaFranja(): string {
    const partes = this.gruposEstado.map((g) => `${g.titulo}, ${g.subtotal}, ${g.pct} por ciento`);
    return `Reparto por estado de las ${this.total} solicitudes del período: ${partes.join('. ')}.`;
  }

  /** Por qué se cayeron las que no llegaron. Es el único lugar donde se ve el
   *  desglose —`RECHAZADA_EN_VERIFICACION` es el estado más frecuente medido— y
   *  el corte de arriba sólo dice el total. */
  get desgloseNoLlegaron(): string {
    const porEstado = this.resumen?.por_estado || {};
    return this.ESTADOS_NO_LLEGARON
      .map((c) => ({ clave: c, valor: porEstado[c] || 0 }))
      .filter((x) => x.valor > 0)
      .sort((a, b) => b.valor - a.valor)
      .map((x) => `${this.textoEstatus(x.clave).toLowerCase()} ${x.valor}`)
      .join(' · ');
  }

  /**
   * Lo que queda del bloque «Cómo quedó el cobro» —siete barras y ~200 px— en un
   * renglón, y con el título en sustantivo: «Resultado del cobro», no una pregunta.
   *
   * 🔴 `COBRO_INCOMPLETO` NO se cuenta acá: ya está en el corte «Trabadas», con su
   * anillo de alerta, y el mismo número dos veces en una pantalla no informa dos
   * veces. Y cuando NINGUNA etapa tiene rastro escrito se dice con todas las
   * letras, porque ahí un «Trabadas 0» no es una buena noticia: es que no se sabe
   * (`hayRastroDeEtapas`).
   */
  get resultadoDelCobro(): string {
    if (!this.total) return '';
    if (!this.hayRastroDeEtapas) {
      return `ninguna de las ${this.total} tiene el rastro escrito, así que no se puede saber si el cobro quedó completo`;
    }
    const porEtapa = this.resumen?.por_etapa || {};
    return Object.keys(porEtapa)
      .filter((c) => c !== 'COBRO_INCOMPLETO' && (porEtapa[c] || 0) > 0)
      .sort((a, b) => (porEtapa[b] || 0) - (porEtapa[a] || 0))
      .map((c) => `${this.textoEtapa(c).toLowerCase()} ${porEtapa[c]}`)
      .join(' · ');
  }

  // ── vocabulario ───────────────────────────────────────────────────────────

  // 🔴 Hallazgo 5: RECHAZADA_EN_VERIFICACION (el motor de riesgo) y RECHAZADA_POR_EL_CLIENTE (el
  // cliente) son la mitad del listado y dos reclamos distintos — el portal (`comercios-front`,
  // `lib/ordenes.ts:estadoEnPalabras`) ya los separa bien; se copia ese mismo criterio acá.
  readonly TEXTO_ESTATUS: any = {
    EN_ESPERA: 'En espera', ACEPTADA: 'Aceptada', CONFIRMADA: 'Confirmada',
    ANULADA: 'Anulada', VENCIDA: 'Vencida', EN_PROCESO: 'En proceso', CANCELADA: 'Cancelada',
    RECHAZADA_POR_EL_CLIENTE: 'Rechazada por el cliente',
    RECHAZADA_EN_VERIFICACION: 'Rechazada en verificación',
  };

  textoEstatus = (status: string): string =>
    this.TEXTO_ESTATUS[status] || (String(status || '').startsWith('RECHAZADA') ? 'Rechazada' : (status || '—'));

  // 🔴 Hallazgo 7: las 7 etapas son de negocio (`infra/nucleo/rastro.js:ETAPAS`), no un código
  // para leer crudo — mismo vocabulario que ya certificó el portal (`lib/ordenes.ts:etapaEnPalabras`).
  readonly TEXTO_ETAPA: any = {
    CREDITO_INTENTO: 'Creando el crédito', CREDITO_CREADO: 'Crédito creado',
    CREDITO_FALLIDO: 'El crédito no llegó a crearse', COBRO_INTENTO: 'Asentando el cobro',
    COBRO_ASENTADO: 'Cobro asentado', COBRO_INCOMPLETO: 'Cobro incompleto', COBRO_FALLIDO: 'El cobro falló',
  };

  textoEtapa = (etapa: string): string => this.TEXTO_ETAPA[etapa] || (etapa || '—');

  /**
   * 🔴 EL CASO CARO (documentos 63 §3.5 y 66 §2): `status` y `etapa` se funden en UNA
   * pastilla de negocio, con su renglón que la explica. Una venta sana (`CONFIRMADA` +
   * `COBRO_ASENTADO`) y una con la plata cobrada y el crédito a medias (`CONFIRMADA` +
   * `COBRO_INCOMPLETO`/`COBRO_FALLIDO`/`CREDITO_FALLIDO`) pintaban la misma pastilla verde
   * y se distinguían por una segunda pastilla en otra columna. Ahora la segunda se llama
   * «Trabada» y se ve distinta de un vistazo, con anillo (documento 66 §7 decisión 1).
   *
   * `closed_reason` ya viaja en el listado (`listado-solicitudes.js:aItem`, agregado en la
   * certificación del 2026-09-20): es lo único que explica por qué se cerró una solicitud, y
   * se muestra tal cual lo escribió el servidor — nunca se inventa un motivo cuando falta.
   */
  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * 🔴 EL MOTIVO DEL CIERRE, EN CASTELLANO — no el código crudo
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * `closed_reason` viaja como el CÓDIGO del módulo, y la tabla lo estaba
   * pintando tal cual: debajo de «Rechazada en verificación» salía
   * `CUPO_INACTIVO`, en mayúsculas y con guión bajo. Lo vi en pantalla el
   * 2026-09-21. Un identificador interno a la vista del usuario no explica nada
   * —hay que saberse el catálogo— y encima se lee como un error del sistema.
   *
   * Las frases son LAS MISMAS que el servidor tiene en
   * `modules/integracion-pos/dominio/codigos.js`. Están copiadas y no leídas de
   * la respuesta porque el listado manda el código, no el mensaje; el día que
   * mande los dos, esto se borra y se usa el suyo.
   *
   * ⚠️ Un código que no esté en este mapa se muestra tal cual, no se esconde:
   * un motivo que el front no sabe traducir sigue siendo información, y
   * callarlo dejaría la fila sin explicación ninguna.
   */
  private readonly MOTIVOS: { [k: string]: string } = {
    CLIENTE_NO_EXISTE:                 'El cliente no está registrado.',
    CLIENTE_INACTIVO:                  'El cliente no está habilitado para financiar.',
    CLIENTE_SIN_PLAN_ASIGNADO:         'El cliente no tiene planes asignados.',
    MONTO_FUERA_DE_PLAN:               'El monto no entra en ningún plan.',
    SIN_PLAN_APLICABLE:                'No hay un plan aplicable para esta compra.',
    CUPO_INSUFICIENTE:                 'No alcanza el cupo disponible.',
    CUPO_NO_HABILITADO:                'El cliente no habilitó su cupo.',
    CUPO_INACTIVO:                     'El cupo del cliente está inactivo.',
    PAGARE_RECHAZADO_POR_EL_CLIENTE:   'El cliente rechazó la operación.',
    OPERACION_NO_PERTENECE_AL_CLIENTE: 'La operación no corresponde a esa cédula.',
    CODIGO_AUTORIZACION_VENCIDO:       'El código de autorización venció.',
    PLAZO_DE_COBRO_VENCIDO:            'Pasó el plazo para cobrarla: hay que hacerla de nuevo.',
  };

  private motivo = (item: any): string => {
    const c = String(item?.closed_reason || '').trim();
    if (!c) { return ''; }
    return this.MOTIVOS[c] || c;
  };

  estadoNegocio = (item: any): { texto: string; clase: string; anillo: boolean; renglon: string } => {
    const status = item?.status;
    const etapa = item?.etapa;

    if (status === 'CONFIRMADA') {
      if (etapa && this.ETAPAS_TRABADA.includes(etapa)) {
        return { texto: 'Trabada', clase: 'kb-badge-alerta', anillo: true, renglon: 'Se cobró y el crédito no quedó firme.' };
      }
      if (!etapa) {
        return { texto: 'Confirmada', clase: 'kb-badge-contorno', anillo: false, renglon: 'Sin rastro de etapas.' };
      }
      return { texto: 'Confirmada', clase: 'kb-badge-exito', anillo: false, renglon: '' };
    }

    if (status === 'ACEPTADA') {
      const renglon = ['Falta que la caja cobre.', this.textoVence(item)].filter(Boolean).join(' ');
      return { texto: 'Aceptada', clase: 'kb-badge-alerta', anillo: false, renglon };
    }

    if (status === 'EN_ESPERA' || status === 'EN_PROCESO') {
      return { texto: this.textoEstatus(status), clase: 'kb-badge-neutro', anillo: false, renglon: '' };
    }

    if (status === 'VENCIDA') {
      return { texto: 'Vencida', clase: 'kb-badge-alerta', anillo: false, renglon: this.motivo(item) };
    }

    if (status === 'ANULADA' || status === 'CANCELADA') {
      return { texto: this.textoEstatus(status), clase: 'kb-badge-peligro', anillo: false, renglon: this.motivo(item) };
    }

    if (String(status || '').startsWith('RECHAZADA')) {
      return { texto: this.textoEstatus(status), clase: 'kb-badge-peligro', anillo: false, renglon: this.motivo(item) };
    }

    return { texto: this.textoEstatus(status), clase: 'kb-badge-neutro', anillo: false, renglon: '' };
  };

  /** Cuánto falta para que venza una ACEPTADA. `vence` (`expires_at`) sólo viaja si
   *  sigue viva (contrato §4.1) — es el dato que dice que una venta se pierde en tres
   *  horas, y hoy la tabla no lo pintaba. */
  private textoVence = (item: any): string => {
    if (!item?.vence) return '';
    const ms = new Date(item.vence).getTime() - Date.now();
    if (ms <= 0) return 'Ya venció.';
    const horas = ms / 3_600_000;
    if (horas < 1) return `Vence en ${Math.max(1, Math.round(ms / 60000))} min.`;
    if (horas < 48) return `Vence en ${Math.round(horas)} h.`;
    return `Vence en ${Math.round(horas / 24)} días.`;
  };

  formatoMonto = (n: any): string =>
    (n === null || n === undefined) ? '—' : `USD ${Number(n).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  /** La tienda por su nombre, sin repetir el comercio que ya va arriba («MundoTotal · Ventas en línea»
   *  debajo de «MundoTotal» → «Ventas en línea»). Sin nombre, por su código. */
  tiendaSinComercio = (it: any): string => {
    const n = String(it?.tienda_nombre || '').trim();
    if (!n) return `Tienda ${it?.tienda}`;
    const c = String(it?.comercio?.name || '').trim();
    return c && n.toLowerCase().startsWith(`${c.toLowerCase()} · `) ? n.slice(c.length + 3) : n;
  };

  /** «V19163767» → «V-19.163.767», como en el portal. Lo que no parece cédula, tal cual. */
  cedula = (v: any): string => {
    const m = String(v ?? '').trim().toUpperCase().match(/^([VEJPG])-?(\d{5,9})$/);
    if (!m) return v ? String(v) : '—';
    return `${m[1]}-${Number(m[2]).toLocaleString('es-VE')}`;
  };

  /** «JUAN SOTO» o «juan soto» → «Juan Soto»; lo bien escrito, tal cual. */
  nombrePropio = (s: any): string => {
    const t = String(s || '').trim();
    if (!(t === t.toLowerCase() || t === t.toUpperCase())) return t;
    return t.toLowerCase().replace(/(^|\s)(\p{L})/gu, (_: string, a: string, b: string) => a + b.toUpperCase());
  };

  // ── paginación (server-side: `total`/`page`/`limit` los pone el back) ────

  get paginaActual(): number { return this.resultado?.page || this.filtros.page || 1; }
  get porPagina(): number { return this.resultado?.limit || this.filtros.limit || 50; }
  get totalPaginas(): number { return Math.max(1, Math.ceil(this.total / this.porPagina)); }
  get desdeFila(): number { return this.total ? (this.paginaActual - 1) * this.porPagina + 1 : 0; }
  get hastaFila(): number { return Math.min(this.paginaActual * this.porPagina, this.total); }

  irAPagina = (n: number): void => {
    if (n < 1 || n > this.totalPaginas || n === this.paginaActual) return;
    this.filtros.page = n;
    this.actualizarUrl({ reiniciarPagina: false });
  };

  // ── navegación ────────────────────────────────────────────────────────────

  abrirDetalle = (item: any): void => {
    this.router.navigate(['/console/solicitudes-credito', item.code]);
  };
}
